import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "@/context/ThemeContext";
import { Logo } from "@/components/Logo";
import { PlanChip } from "@/components/PlanChip";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Bookmark, History, User, LogOut, Sparkles, Layers, Sun, Moon } from "lucide-react";

function ThemeToggle() {
  const { theme, toggle } = useThemeMode();
  const dark = theme === "dark";
  return (
    <button
      onClick={toggle}
      data-testid="theme-toggle"
      aria-label="Toggle theme"
      className="w-9 h-9 rounded-full bg-canvas-alt text-ink-muted hover:text-ink flex items-center justify-center transition-colors"
    >
      {dark ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}

export function Navbar() {
  const { user, usage, logout } = useAuth();
  const { openAuth } = useModals();
  const navigate = useNavigate();
  const location = useLocation();
  const onLanding = location.pathname === "/";

  return (
    <header className="sticky top-0 z-40 bg-[var(--surface)]/80 backdrop-blur-xl border-b hairline">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-7">
          <Logo />
          {onLanding && (
            <nav className="hidden md:flex items-center gap-6 text-[14px] font-medium text-ink-muted">
              <a href="#how" className="hover:text-ink transition-colors">How it works</a>
              <Link to="/pricing" className="hover:text-ink transition-colors">Pricing</Link>
              <Link to="/business-name-generator" className="hover:text-ink transition-colors">Generators</Link>
            </nav>
          )}
        </div>

        <div className="flex items-center gap-2.5">
          {usage && <PlanChip usage={usage} className="hidden sm:inline-flex" />}
          <ThemeToggle />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full pl-1 pr-1 py-1 hover:bg-canvas-alt transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <span className="w-8 h-8 rounded-full brand-gradient text-white flex items-center justify-center text-[13px] font-bold">
                    {(user.name || user.email || "U").slice(0, 1).toUpperCase()}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 rounded-lg">
                <div className="px-2 py-2">
                  <p className="text-[13px] font-semibold text-ink truncate">{user.name || "Your account"}</p>
                  <p className="text-[12px] text-ink-subtle truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/saved")} data-testid="menu-saved">
                  <Bookmark size={15} className="mr-2" /> Saved
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/history")} data-testid="menu-history">
                  <History size={15} className="mr-2" /> History
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/bulk")} data-testid="menu-bulk">
                  <Layers size={15} className="mr-2" /> Bulk check
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User size={15} className="mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              className="hidden sm:inline-flex text-[14px] font-semibold text-ink-muted hover:text-ink"
              onClick={() => openAuth({ mode: "login" })}
              data-testid="nav-login-btn"
            >
              Log in
            </Button>
          )}
          <Button
            className="h-9 rounded-lg bg-brand hover:bg-brand-dark text-white font-bold btn-glow text-[14px]"
            onClick={() => navigate("/generate")}
            data-testid="nav-cta-btn"
          >
            <Sparkles size={15} className="mr-1.5" /> Find My Name
          </Button>
        </div>
      </div>
    </header>
  );
}
